import '../css/app.css';

import SessionExpiredModal from '@/components/modals/session-expired-modal';
import DateProvider from '@/providers/date-provider';
import SearchProvider from '@/providers/search-provider';
import { GlobalEvent } from '@inertiajs/core';
import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';
import './bootstrap';
import { initializeTheme } from './hooks/use-appearance';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

/**
 * Handle 419 (CSRF token mismatch) and 401 (Unauthorized) errors from Inertia.
 * This prevents Inertia's default modal/system alert and shows our custom modal.
 */
const handlePageError = (event: Event) => {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail;
    const status =
        detail?.response?.status || detail?.exception?.response?.status;
    if (status === 419 || status === 401) {
        event.preventDefault();
        window.dispatchEvent(new Event('session-expired'));
        return false;
    }
};

router.on('invalid', (event: GlobalEvent<'invalid'>) => {
    const status = event.detail.response.status;
    if (status === 419 || status === 401) {
        event.preventDefault();
        window.dispatchEvent(new Event('session-expired'));
        return false;
    }
});

router.on('exception', (event: GlobalEvent<'exception'>) => {
    const detail = event.detail as unknown as {
        exception?: unknown;
        error?: unknown;
    };
    const error = detail.exception || detail.error;

    if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        (error.response.status === 419 || error.response.status === 401)
    ) {
        event.preventDefault();
        window.dispatchEvent(new Event('session-expired'));
        return false;
    }
});

// Also add document-level listeners as a fallback for some environments
if (typeof document !== 'undefined') {
    document.addEventListener('inertia:invalid', handlePageError);
    document.addEventListener('inertia:exception', handlePageError);
}

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) =>
        resolvePageComponent(
            `./pages/${name}.tsx`,
            import.meta.glob('./pages/**/*.tsx'),
        ),
    setup({ el, App, props }) {
        const root = createRoot(el);

        root.render(
            <DateProvider>
                <SearchProvider>
                    <SessionExpiredModal />
                    <App {...props} />
                </SearchProvider>
            </DateProvider>,
        );
    },
    progress: {
        color: '#4B5563',
    },
});

// This will set light / dark mode on load...
initializeTheme();
